import { faPenToSquare, faPlayCircle } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatDistance, subSeconds } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatDateString, handleError } from "../../../common/utils";
import { InlineSpinner } from "../../../components/inline-spinner";
import MatterChecklist from "../../../components/matter-checklist.component";
import MatterDetailsActions from "../../../components/matter-details-actions";
import HeaderNavBar from "../../../components/header-nav-bar";
import MatterParameters from "../../../components/matter-parameters.component";
import MatterProgress from "../../../components/matter-progress.component";
import MatterUploadSignatures from "../../../components/matter-upload-signatures.component";
import MatterVoterStatus from "../../../components/matter-voter-status.component";
import MatterUploadCirculators from "../../../components/upload-circulators.component";
import { useMatterContext } from "../../../contexts/MatterContext";
import { useAuthGuard } from "../../../hooks/useAuthGuard";
import { useChecklistWithRefresh } from "../../../hooks/useChecklistWithRefresh";
import { useProgressWithRefresh } from "../../../hooks/useProgressWithRefresh";
import { useMatterParametersService } from "../../../services/matter-parameters.service";
import { useRuleService } from "../../../services/rule.service";
import { ExecutionStatus } from "../../../types/background-operation.types";
import { MatterVariable } from "../../../types/matter.types";
import { RuleSummary } from "../../../types/rule.types";
import { Roles } from "../../../types/user.types";
import { Loader } from "../../../components/loader";

export default function MatterDetails() {
  useAuthGuard(Roles.Admin);
  const { matter, error: matterError } = useMatterContext();
  const { runRules, runSpecificRules, getAllRules, getLastRunRulesOperation, getBackgroundOperationStatus } = useRuleService();
  const { getMatterParameters } = useMatterParametersService();

  const [loading, setLoading] = useState(false);
  const [isRunningRules, setIsRunningRules] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { checklist, refreshChecklist } = useChecklistWithRefresh();
  const [matterParameters, setMatterParameters] = useState<MatterVariable[]>([]);

  const [backgroundId, setBackgroundId] = useState<number>(0);

  const [isTimerActive, setIsTimerActive] = useState(false);
  const interval = useRef<NodeJS.Timeout | null>(null);
  const [timeSinceLastRun, setTimeSinceLastRun] = useState<string>("");
  const [ruleRunCompletionTimeStamp, setRuleRunCompletionTimeStamp] = useState<Date | undefined>(undefined);
  const [showRuleSelectionDialog, setShowRuleSelectionDialog] = useState(false);
  const [availableRules, setAvailableRules] = useState<RuleSummary[]>([]);
  const [selectedRuleIds, setSelectedRuleIds] = useState<number[]>([]);

  const {
    progress,
    isLoading: isProgressLoading,
    errorMessage: progressErrorMessage,
    refreshProgress,
  } = useProgressWithRefresh();

  const params = useParams();
  const matterId = params?.matterId || "";
  const isNew = matterId === "new";

  const loadSecondsSinceRuleRunComplete = useCallback(() => {
    getLastRunRulesOperation(matterId).then((lastRun) => {
      if (!lastRun) {
        setTimeSinceLastRun("");
        return;
      }
      if (lastRun.executionStatus === ExecutionStatus.Running) {
        const pastDate = subSeconds(new Date(), lastRun.secondsSinceStarted || 1);
        const timeAgo = formatDistance(pastDate, new Date(), {
          addSuffix: true,
        });
        setTimeSinceLastRun(`Started: ${timeAgo}`);
      } else if (
        lastRun.executionStatus === ExecutionStatus.Succeded ||
        lastRun.executionStatus === ExecutionStatus.Failed
      ) {
        const pastDate = subSeconds(new Date(), lastRun.secondsSinceCompletion || 1);
        const timeAgo = formatDistance(pastDate, new Date(), {
          addSuffix: true,
        });
        setTimeSinceLastRun(`${ExecutionStatus[lastRun.executionStatus]}: ${timeAgo}`);
      }
    });
  }, [getLastRunRulesOperation, matterId]);

  useEffect(() => {
    loadSecondsSinceRuleRunComplete();
  }, [loadSecondsSinceRuleRunComplete]);

  const loadParameters = useCallback(() => {
    setErrorMessage("");
    setLoading(true);

    getMatterParameters(matterId?.toString() || "").then(
      (parameters) => {
        setLoading(false);
        setMatterParameters(parameters);
      },
      (error) => {
        handleError(error, setErrorMessage, setLoading);
      }
    );
  }, [matterId, getMatterParameters]);

  useEffect(() => {
    if (matterError) {
      setErrorMessage(matterError);
    }
  }, [matterError]);

  useEffect(() => {
    if (matter && matterId && matterId !== "new") {
      loadParameters();
      refreshProgress();
    }
  }, [matter, matterId, loadParameters, refreshProgress]);

  const handleRefresh = async () => {
    refreshChecklist();
    loadParameters();
  };

  const handleRunRules = async () => {
    setIsRunningRules(true);
    const localBackgroundId = await runRules(matterId!);
    setBackgroundId(localBackgroundId);
    setIsTimerActive(true);
  };

  const handleRunSelectRules = async () => {
    try {
      const rules = await getAllRules(matterId!);
      setAvailableRules(rules);
      setSelectedRuleIds([]);
      setShowRuleSelectionDialog(true);
    } catch (error) {
      handleError(error, setErrorMessage);
    }
  };

  const handleRuleSelectionChange = (ruleId: number, isSelected: boolean) => {
    if (isSelected) {
      setSelectedRuleIds(prev => [...prev, ruleId]);
    } else {
      setSelectedRuleIds(prev => prev.filter(id => id !== ruleId));
    }
  };

  const handleConfirmRuleSelection = async () => {
    if (selectedRuleIds.length === 0) {
      return;
    }
    
    setShowRuleSelectionDialog(false);
    setIsRunningRules(true);
    
    try {
      const localBackgroundId = await runSpecificRules(matterId!, selectedRuleIds);
      setBackgroundId(localBackgroundId);
      setIsTimerActive(true);
    } catch (error) {
      handleError(error, setErrorMessage);
      setIsRunningRules(false);
    }
  };

  const handleCancelRuleSelection = () => {
    setShowRuleSelectionDialog(false);
    setSelectedRuleIds([]);
  };

  const checkRunRuleProgress = useCallback(async () => {
    try {
      const executionStatus = await getBackgroundOperationStatus(backgroundId);
      if (executionStatus !== ExecutionStatus.Running) {
        setIsTimerActive(false);
        setIsRunningRules(false);
        setRuleRunCompletionTimeStamp(new Date());
        loadSecondsSinceRuleRunComplete();
        refreshProgress();
      }
    } catch (error) {
      return error;
    }
  }, [backgroundId, getBackgroundOperationStatus, loadSecondsSinceRuleRunComplete, refreshProgress]);

  useEffect(() => {
    if (isTimerActive) {
      interval.current = setInterval(checkRunRuleProgress, 3000);
    } else {
      if (interval.current) {
        clearInterval(interval.current);
      }
      interval.current = null;
    }

    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [checkRunRuleProgress, isTimerActive]);

  if (!isNew && (!matterId || !matter)) {
    return <Loader />;
  }

  return (
    <div className="container">
      <HeaderNavBar
        backTo="dashboard"
        shouldHideMatter={true}
        rightContent={!isNew ? <MatterDetailsActions ruleRunCompletionTimeStamp={ruleRunCompletionTimeStamp} onRunSelectRules={handleRunSelectRules} /> : undefined}
      />
      <div className="row tablet-view">
        <div className="col-4 tablet-left d-flex flex-column">
          <div className="tablet-buffer d-flex flex-fill flex-column mb-3">
            <div>
              <span>Matter</span>
              <h2>{matter?.name || "New"}</h2>
              {!isNew && (
                <>
                  <span>
                    {matter?.numberSignaturesRequired} due by {formatDateString(matter?.dueDate?.toString())}
                  </span>
                  <Link role="button" to={`/admin/matters/${matterId}`}>
                    <FontAwesomeIcon icon={faPenToSquare} className="ms-2" />
                  </Link>
                </>
              )}
            </div>
            {!isNew && (
              <>
                <div className="mt-4 mb-4">
                  <MatterChecklist checklist={checklist} refreshChecklist={refreshChecklist} />
                </div>
                <div>
                  <button className="btn btn-light btn-outline" onClick={handleRunRules}>
                    {isRunningRules ? <InlineSpinner /> : <FontAwesomeIcon icon={faPlayCircle} className="me-2" />}
                    Run Rules
                  </button>
                </div>
                {!!timeSinceLastRun && <div className="mt-2">{timeSinceLastRun}</div>}
              </>
            )}
          </div>
        </div>
        <div className="col-8 tablet-right">
          <div className="tablet-buffer">
            <MatterProgress progress={progress} isLoading={isProgressLoading} errorMessage={progressErrorMessage} />
            <MatterVoterStatus />

            <MatterUploadSignatures matter={matter} refreshChecklist={handleRefresh} />

            <MatterParameters refreshChecklist={handleRefresh} matterParameters={matterParameters} />
            <MatterUploadCirculators refreshChecklist={refreshChecklist} />
          </div>
        </div>
      </div>

      {/* Rule Selection Modal */}
      {showRuleSelectionDialog && (
        <div className="modal fade show" style={{ display: 'block' }} aria-modal="true" role="dialog">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Rules to Run</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancelRuleSelection}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {availableRules.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <p>No rules found for this matter.</p>
                    <p>Rules need to be created before they can be executed.</p>
                  </div>
                ) : (
                  <div className="form-check-list">
                    {availableRules.map((rule) => (
                      <div key={rule.id} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`rule-${rule.id}`}
                          checked={selectedRuleIds.includes(rule.id)}
                          onChange={(e) => handleRuleSelectionChange(rule.id, e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor={`rule-${rule.id}`}>
                          {rule.name}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light btn-outline"
                  onClick={handleCancelRuleSelection}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sw btn-outline"
                  onClick={handleConfirmRuleSelection}
                  disabled={selectedRuleIds.length === 0 || availableRules.length === 0}
                >
                  {availableRules.length === 0 ? 'No Rules Available' : `Run Selected Rules (${selectedRuleIds.length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showRuleSelectionDialog && <div className="modal-backdrop fade show"></div>}
    </div>
  );
}
